<template>
  <v-row>
    <v-col xs="12" sm="8" md="4">
      <v-alert icon="view_list" color="primary" text>
        <v-flex style="float: left">Product Categories</v-flex>
      </v-alert>
      <v-list>
        <v-list-group
          v-for="category in categories"
          :key="category._id"
          no-action
        >
          <template v-slot:activator>
            <v-list-item-content @click="activeCategory = category">
              <v-list-item-title v-text="category.name"></v-list-item-title>
            </v-list-item-content>
          </template>
          <v-list-item
            v-for="subcategory in category.subcategories"
            :key="subcategory._id"
          >
            <v-list-item-content>
              <v-list-item-title v-text="subcategory.name"></v-list-item-title>
            </v-list-item-content>
          </v-list-item>
          <v-list-item
            v-if="canAddCategory"
            @click="
              addSubcategoryModal = true;
              categoryId = category._id;
            "
          >
            <v-list-item-content>
              <v-list-item-title>
                <v-icon>add</v-icon>Add Subcategory
              </v-list-item-title>
            </v-list-item-content>
          </v-list-item>
        </v-list-group>
      </v-list>
    </v-col>
    <v-col xs="12">
      <v-row v-if="canAddCategory">
        <v-btn color="primary" @click="addCategoryModal = true"
          >Add Category</v-btn
        >
      </v-row>
      <v-row
        class="mt-9"
        v-if="activeCategory && activeCategory.fieldSettings.length"
      >
        <v-card>
          <v-card-text>Fields Settings</v-card-text>
          <div class="pl-4 pb-0">
            <v-row
              v-for="(field, index) in activeCategory.fieldSettings"
              :key="index"
            >
              <v-col>
                <v-text-field readonly label="Key" :value="field.key" dense />
              </v-col>
              <v-col>
                <v-text-field
                  label="Text"
                  :readonly="!canUpdateCategory"
                  @blur="saveCategory"
                  v-model="field.text"
                  dense
                />
              </v-col>
              <v-col>
                <v-switch
                  label="Show Default"
                  @change="saveCategory"
                  :disabled="!canUpdateCategory"
                  v-model="activeCategory.fieldSettings[index].showDefault"
                />
              </v-col>
              <v-col>
                <v-switch
                  label="Sortable"
                  @change="saveCategory"
                  :disabled="!canUpdateCategory"
                  v-model="activeCategory.fieldSettings[index].sortable"
                />
              </v-col>
            </v-row>
          </div>
        </v-card>
      </v-row>
    </v-col>
    <add-category-modal
      v-if="addCategoryModal"
      title="Add Product Category"
      :addCategoryModal.sync="addCategoryModal"
      @addCategory="saveNewCategory"
    />
    <add-subcategory-modal
      v-if="addSubcategoryModal"
      title="Add Product Subcategory"
      :addSubcategoryModal.sync="addSubcategoryModal"
      @addSubcategories="addSubcategories"
    />
  </v-row>
</template>

<script>
import request from "@/modules/request";
import AddCategoryModal from "@/components/shared/modals/AddCategoryModal";
import AddSubcategoryModal from "@/components/shared/modals/AddSubcategoryModal";
import { hasPermission } from "@/modules/rbac/permission-manifest";

export default {
  name: "ProductCategories",
  inject: ["snackBar", "reactiveInjected"],
  async mounted() {
    await this.loadData();
  },
  components: {
    AddCategoryModal,
    AddSubcategoryModal,
  },
  data() {
    return {
      categories: [],
      categoryId: null,
      addCategoryModal: false,
      addSubcategoryModal: false,
      activeCategory: null,
      canAddCategory: hasPermission(
        "canCreateProduct",
        null,
        this.reactiveInjected.activeUser
      ),
      canUpdateCategory: hasPermission(
        "canUpdateProduct",
        null,
        this.reactiveInjected.activeUser
      ),
    };
  },
  methods: {
    async loadData() {
      const { getCategories } = this;
      await Promise.all([getCategories()]).then((results) => {
        const [categories] = results;
        this.categories = categories;
      });
    },
    async getCategories() {
      return request
        .callInternal({
          method: "get",
          route: "/api/admin/fullcategories",
        })
        .then((results) => {
          return results.data;
        });
    },
    async saveCategory() {
      const { activeCategory } = this;
      await request.callInternal({
        method: "put",
        route: `/api/admin/category/${activeCategory._id}`,
        data: activeCategory,
      });
      this.snackBar("Saved!", "success");
    },
    async saveNewCategory(category) {
      let newSubcategoriesWithDefaultMarkups;
      const { data: newCategory } = await request
        .callInternal({
          method: "post",
          route: "/api/admin/category",
          data: category,
        })
        .catch((error) => {
          console.error("error adding category", error);
          return error;
        });

      if (category.subcategories.length) {
        const subcategoryProms = category.subcategories.map(async (sub) => {
          const { _id } = newCategory;
          const { name } = sub;
          return this.addSubcategory(name, _id);
        });

        const newSubcategories = await Promise.all(subcategoryProms).catch(
          (error) => {
            console.error("add subcategory promises error", error);
          }
        );

        newSubcategoriesWithDefaultMarkups = newSubcategories.map(
          (sub, index) => {
            sub.defaultMarkup = category.subcategories[index].defaultMarkup;
            return sub;
          }
        );
      }

      const results = await request.callInternal({
        route: `/api/admin/companycategorymarkup`,
        method: "post",
        data: {
          newCategory,
          newSubcategories: newSubcategoriesWithDefaultMarkups || [],
          defaultCategoryMarkup: category.defaultMarkup,
        },
      });

      this.snackBar("Category added!", "success");
      this.addCategoryModal = false;
      this.loadData();
    },
    async addSubcategories(subcategories) {
      const { categoryId } = this;
      const proms = subcategories.map(async (sub) => {
        const { name } = sub;
        return this.addSubcategory(name, categoryId);
      });

      const newSubcategories = await Promise.all(proms).catch((error) => {
        console.log("Add subcategories error...", error);
      });

      await request
        .callInternal({
          method: "post",
          route: "/api/admin/companysubcategorymarkup",
          data: {
            category: categoryId,
            newSubcategories,
          },
        })
        .catch((error) => {
          console.error("Eror adding subcategory to category", error);
        });

      this.snackBar("Added subcategory!", "success");
      this.addSubcategoryModal = false;
      await this.loadData();
      this.product.subcategory = newSubcategories[0];
      this.setCategory(
        this.categories.find((category) => category._id === categoryId)
      );
    },
    async addSubcategory(name, category) {
      const { data: newSubcategory } = await request
        .callInternal({
          method: "post",
          route: "/api/admin/subcategory",
          data: {
            name,
            category,
          },
        })
        .catch((error) => {
          console.error("Add subcategory error", error);
          return error;
        });
      return newSubcategory;
    },
  },
};
</script>

<style scoped></style>
